import { forwardRef } from 'react'

import { Avatar, Box, Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import TextArea from 'modules/core/components/TextArea'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Return } from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { useEngagementFeatureAvailability } from 'modules/engagement/hooks/useEngagementFeatureAvailability'

interface Props {
  saving: boolean
  onSubmit: () => Promise<void | Return<void>>
  comment: string | null
  setComment: (value: string) => void
  setUsersIds: (value: number[]) => void
}

const CommentInput = forwardRef<EditorHandle, Props>(function CommentInput(
  { saving, onSubmit, setComment, comment, setUsersIds },
  ref
) {
  const {
    access: { fullName, avatar },
  } = useCurrentSession()
  const hasClaps = useEngagementFeatureAvailability(Features.communities_claps)

  // TODO: move this to a lazy GraphQL query
  const mentioningUsers = users.active.map((user) => ({
    id: user.attributes.id,
    employeeId: user.employee?.id,
    fullname: user.fullName(),
    picture: user.attributes.avatar ?? '',
  }))

  return (
    <Box width="full" gap="s24" flexDirection="row" paddingBottom="s24">
      <Avatar url={buildUrl(avatar)} label={fullName} size="s48" />
      <Box
        paddingX="s24"
        paddingY="s16"
        background="grey200"
        borderRadius={{
          all: 'abs008',
        }}
        width="full"
      >
        {hasClaps ? (
          <TextEditor
            ref={ref}
            initialEditorState={{ data: comment }}
            onChange={(data) => {
              if (data && typeof data === 'object') {
                setComment(data.value)
                setUsersIds(data?.ids)
              }
            }}
            placeholder={i18n.t('posts.comments.placeholder')}
            height="s96"
            isRichText={false}
            hasMentions
            users={mentioningUsers}
          />
        ) : (
          <TextArea
            placeholder={i18n.t('posts.comments.placeholder')}
            value={comment ?? ''}
            onChange={(value) => setComment(value)}
          />
        )}
        <Box paddingTop="s16" flexDirection="rowReverse">
          <Button type="secondary" disabled={saving || comment === ''} onClick={onSubmit}>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
})

export default observer(CommentInput)
